import { Box, Button, TextField, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { tokens } from '../../theme';

const CoordinatorReports = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [countryId, setCountryId] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (countryId) params.append('countryId', countryId);
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/coordinator-reports?${params.toString()}`, {
        headers: { token },
      });
      const data = await res.json();
      setRows(data?.success ? data.data || [] : []);
    } catch (e) {
      console.error('Error fetching coordinator reports', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { field: 'coordinatorUsername', headerName: 'Coordinator', flex: 1.2, minWidth: 180 },
    { field: 'totalPassengers', headerName: 'Total', flex: 0.6, minWidth: 90 },
    { field: 'scannedPassengers', headerName: 'Scanned', flex: 0.6, minWidth: 100 },
    {
      field: 'notScannedPassengers',
      headerName: 'Not Scanned',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => (
        <Link to={`/reports/coordinators/${params.row.coordinatorId}/not-scanned`}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.redAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[700] },
            }}
          >
            {params.value}
          </Button>
        </Link>
      ),
    },
    {
      field: 'view',
      headerName: 'View',
      sortable: false,
      filterable: false,
      minWidth: 160,
      renderCell: (params) => (
        <Link
          to={
            countryId
              ? `/reports/coordinators/${params.row.coordinatorId}/destination/${countryId}`
              : `/reports/coordinators/${params.row.coordinatorId}/destination/0`
          }
          onClick={(e) => {
            if (!countryId) e.preventDefault();
          }}
        >
          <Button
            variant="contained"
            disabled={!countryId}
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
            }}
          >
            Destination Report
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header title="COORDINATOR REPORTS" subtitle="Scanned / Not Scanned summary by coordinator" />

      <Box
        mb={2}
        p={2}
        display="flex"
        flexWrap="wrap"
        gap={2}
        sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}
      >
        <TextField
          label="Destination ID (optional)"
          size="small"
          value={countryId}
          onChange={(e) => setCountryId(e.target.value)}
          sx={{ backgroundColor: colors.primary[500] }}
        />
        <TextField
          type="date"
          label="Start Date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ backgroundColor: colors.primary[500] }}
        />
        <TextField
          type="date"
          label="End Date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ backgroundColor: colors.primary[500] }}
        />
        <Button variant="outlined" onClick={fetchData} sx={{ borderColor: colors.blueAccent[500], color: colors.blueAccent[500] }}>
          Apply
        </Button>
      </Box>

      <Box
        height="600px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.coordinatorId} loading={loading} />
      </Box>
    </Box>
  );
};

export default CoordinatorReports;

